import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Badge, Tag } from './ui'
import { useT } from '../lib/i18n'
import { useAutoDemo } from '../lib/auto-demo'
import type {
  ChatLocale,
  Locale,
  MatrixRowLocale,
  SopStepLocale,
  ToolCallLocale,
} from '../lib/locales'

type L2SubTrack = 'rag' | 'mcp' | 'skill'

export function L2Demo() {
  const [active, setActive] = useState<L2SubTrack>('rag')
  const [runKey, setRunKey] = useState(0)
  const t = useT()
  const l2 = t.l2
  const autoDemo = useAutoDemo()

  // Auto-demo: switch tabs in response to current step action.
  useEffect(() => {
    const action = autoDemo.currentStep?.action
    if (action?.target === 'l2-tab') {
      setActive(action.value)
    }
  }, [autoDemo.currentStep])

  const tabs: { id: L2SubTrack; label: string; caption: string }[] = [
    { id: 'rag', ...l2.tabs.rag },
    { id: 'mcp', ...l2.tabs.mcp },
    { id: 'skill', ...l2.tabs.skill },
  ]

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {tabs.map((tab) => {
            const isActive = tab.id === active
            return (
              <button
                key={tab.id}
                onClick={() => setActive(tab.id)}
                className={`relative px-4 py-2.5 rounded-[var(--radius-md)] transition-all duration-150 ease-[var(--ease-orion)] text-left group ${
                  isActive
                    ? 'bg-[color:var(--color-orion-night)] border-faint'
                    : 'border border-transparent hover:bg-[color:var(--color-orion-night)]/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className={`mono text-[length:var(--text-small)] font-medium ${
                      isActive ? 'text-[color:var(--color-orion-text)]' : 'text-[color:var(--color-orion-haze)]'
                    }`}
                  >
                    {tab.label}
                  </span>
                  {isActive && (
                    <motion.span
                      layoutId="l2-tab-dot"
                      className="size-1.5 rounded-full bg-[color:var(--color-orion-amber)]"
                    />
                  )}
                </div>
                <div
                  className={`text-[length:var(--text-micro)] ${
                    isActive ? 'text-[color:var(--color-orion-haze)]' : 'text-[color:var(--color-orion-haze)]/70'
                  }`}
                >
                  {tab.caption}
                </div>
              </button>
            )
          })}
        </div>
        <button
          type="button"
          onClick={() => setRunKey((k) => k + 1)}
          className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-2.5 py-1 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-haze)] hover:bg-[color:var(--color-orion-amber)]/15 hover:border-[color:var(--color-orion-amber)]/60 hover:text-[color:var(--color-orion-amber)] transition-colors"
        >
          {l2.rerunButton}
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${active}-${runKey}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        >
          {active === 'rag' && <RagView l2={l2} />}
          {active === 'mcp' && <McpView l2={l2} />}
          {active === 'skill' && <SkillView l2={l2} />}
        </motion.div>
      </AnimatePresence>

      <div
        data-demo-target="l2-limitation"
        className="surface-night border-faint border-l-2 border-l-[color:var(--color-orion-block)]/60 rounded-[var(--radius-lg)] p-4 sm:p-5"
      >
        <div className="flex items-start gap-3">
          <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-block)] shrink-0 pt-0.5 uppercase tracking-widest">
            {l2.sharedLimitationLabel}
          </span>
          <p className="text-[color:var(--color-orion-haze)]" style={{ fontSize: 'var(--text-body)' }}>
            {l2.sharedLimitation}
          </p>
        </div>
      </div>
    </div>
  )
}

function RagView({ l2 }: { l2: Locale['l2'] }) {
  const { chat, citations, citationsLabel, inputPlaceholder, protocolTag, hasCitationBadge } = l2.rag
  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-4">
      <div className="surface-night border-faint rounded-[var(--radius-lg)] p-4 sm:p-6 space-y-4">
        {chat.map((b, i) => (
          <ChatLine key={i} bubble={b} index={i} citationBadge={hasCitationBadge} />
        ))}
        <div className="pt-3 border-t border-[color:var(--color-orion-surface)]/50 flex items-center gap-2">
          <div className="flex-1 px-3 py-2 rounded-[var(--radius-md)] border-faint text-[length:var(--text-small)] text-[color:var(--color-orion-haze)] mono">
            {inputPlaceholder}
          </div>
          <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] uppercase tracking-widest">
            {protocolTag}
          </span>
        </div>
      </div>

      <div data-demo-target="l2-rag-citations" className="space-y-2">
        <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
          {citationsLabel}
        </div>
        {citations.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, x: 8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 + i * 0.15, duration: 0.35 }}
            className="surface-card border-faint rounded-[var(--radius-md)] p-3"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className="size-1.5 rounded-full bg-[color:var(--color-orion-cyan)]" />
              <Tag kind="standard" className="text-[length:var(--text-micro)]">
                {c.ref}
              </Tag>
            </div>
            <p className="text-[length:var(--text-small)] text-[color:var(--color-orion-haze)] leading-relaxed">
              {c.quote}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

function McpView({ l2 }: { l2: Locale['l2'] }) {
  const {
    promptMeta,
    userPrompt,
    callStackLabel,
    protocolNote,
    toolCalls,
    matrix,
    matrixTitle,
    matrixBadge,
    matrixHeaders,
  } = l2.mcp
  return (
    <div className="space-y-4">
      <div className="surface-night border-faint rounded-[var(--radius-lg)] p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Badge tone="info" dot>
            {promptMeta}
          </Badge>
        </div>
        <p className="text-[color:var(--color-orion-text)]" style={{ fontSize: 'var(--text-body)' }}>
          {userPrompt}
        </p>
      </div>

      <div
        data-demo-target="l2-mcp-stack"
        className="surface-night border-faint rounded-[var(--radius-lg)] p-4 sm:p-5"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-amber)] uppercase tracking-widest">
            {callStackLabel}
          </span>
          <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
            {protocolNote}
          </span>
        </div>

        <ol className="space-y-2">
          {toolCalls.map((t, i) => (
            <ToolCallRow key={t.id} call={t} index={i} />
          ))}
        </ol>
      </div>

      <ComplianceMatrix
        rows={matrix}
        title={matrixTitle}
        badge={matrixBadge}
        headers={matrixHeaders}
      />
    </div>
  )
}

function ToolCallRow({ call, index }: { call: ToolCallLocale; index: number }) {
  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ delay: 0.25 + index * 0.2, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="flex items-start gap-3 px-3 py-2 rounded-[var(--radius-sm)] hover:bg-[color:var(--color-orion-deep)]/40"
    >
      <div className="flex items-center gap-2 shrink-0 pt-1">
        <span className="size-2 rounded-full bg-[color:var(--color-orion-pass)]" />
        <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] uppercase tracking-widest">
          [{index + 1}]
        </span>
        <Tag kind="standard" className="text-[length:var(--text-micro)]">
          {call.system}
        </Tag>
      </div>
      <div className="flex-1 min-w-0">
        <div className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-text)] break-words">
          {call.description}
        </div>
        {call.resultLine && (
          <div className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] mt-0.5 break-words">
            → {call.resultLine}
          </div>
        )}
      </div>
    </motion.li>
  )
}

interface ComplianceMatrixProps {
  rows: MatrixRowLocale[]
  title: string
  badge: string
  headers: { item: string; note: string }
}

function ComplianceMatrix({ rows, title, badge, headers }: ComplianceMatrixProps) {
  return (
    <div className="surface-night border-faint rounded-[var(--radius-lg)] p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-amber)] uppercase tracking-widest">
          {title}
        </span>
        <Badge tone="block" dot>
          {badge}
        </Badge>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[length:var(--text-small)]">
          <thead>
            <tr className="text-left text-[color:var(--color-orion-haze)] mono uppercase tracking-widest text-[length:var(--text-micro)]">
              <th className="py-1.5 pr-4">{headers.item}</th>
              <th className="py-1.5 px-2 text-center">A</th>
              <th className="py-1.5 px-2 text-center">B</th>
              <th className="py-1.5 px-2 text-center">C</th>
              <th className="py-1.5 pl-4">{headers.note}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-[color:var(--color-orion-surface)]/40">
                <td className="py-2 pr-4 text-[color:var(--color-orion-text)]">{r.item}</td>
                <td className="py-2 px-2 text-center"><Cell v={r.a} /></td>
                <td className="py-2 px-2 text-center"><Cell v={r.b} /></td>
                <td className="py-2 px-2 text-center"><Cell v={r.c} /></td>
                <td className="py-2 pl-4 text-[color:var(--color-orion-haze)]">{r.note ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Cell({ v }: { v: 'pass' | 'block' | 'warn' }) {
  if (v === 'pass') return <span className="text-[color:var(--color-orion-pass)] mono">●</span>
  if (v === 'block') return <span className="text-[color:var(--color-orion-block)] mono">✕</span>
  return <span className="text-[color:var(--color-orion-warn)] mono">!</span>
}

function SkillView({ l2 }: { l2: Locale['l2'] }) {
  const {
    skillPrefix,
    version,
    progressBadge,
    stepRowPrefix,
    steps,
    reportLabel,
    recommendation,
    pendingDecision,
    pendingTag,
  } = l2.skill
  return (
    <div className="space-y-4">
      <div
        data-demo-target="l2-skill-steps"
        className="surface-night border-faint rounded-[var(--radius-lg)] p-4 sm:p-5"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-[color:var(--color-orion-violet)]" />
            <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-text)]">
              {skillPrefix}emergency-spare-compliance-check
            </span>
            <Tag kind="entity" className="text-[length:var(--text-micro)]">
              {version}
            </Tag>
          </div>
          <Badge tone="process" dot>
            {progressBadge}
          </Badge>
        </div>

        <ol className="space-y-1.5">
          {steps.map((s, i) => (
            <SopStepRow key={s.index} step={s} index={i} rowPrefix={stepRowPrefix} />
          ))}
        </ol>
      </div>

      <div className="surface-night border-faint rounded-[var(--radius-lg)] p-4 sm:p-5">
        <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
          {reportLabel}
        </div>
        <p className="text-[color:var(--color-orion-text)]" style={{ fontSize: 'var(--text-body)', lineHeight: 1.6 }}>
          {recommendation}
        </p>
        <div className="mt-3 pt-3 border-t border-[color:var(--color-orion-surface)]/50 flex items-center justify-between gap-3">
          <span className="text-[color:var(--color-orion-haze)] italic" style={{ fontSize: 'var(--text-small)' }}>
            {pendingDecision}
          </span>
          <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
            {pendingTag}
          </span>
        </div>
      </div>
    </div>
  )
}

function SopStepRow({ step, index, rowPrefix }: { step: SopStepLocale; index: number; rowPrefix: string }) {
  const label = rowPrefix.replace('{idx}', String(step.index).padStart(2, '0'))
  return (
    <motion.li
      initial={{ opacity: 0, x: -6 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ delay: 0.2 + index * 0.08, duration: 0.3 }}
      className="flex items-center gap-3 text-[length:var(--text-small)]"
    >
      <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)] w-14 shrink-0">
        {label}
      </span>
      <span className="text-[color:var(--color-orion-text)] flex-1">{step.title}</span>
      <span className="text-[color:var(--color-orion-pass)] mono">✓</span>
    </motion.li>
  )
}

function ChatLine({
  bubble,
  index,
  citationBadge,
}: {
  bubble: ChatLocale
  index: number
  citationBadge: string
}) {
  const isUser = bubble.who === 'user'
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ delay: 0.3 + index * 0.6, duration: 0.4 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`max-w-[88%] flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}>
        <div
          className={`mono text-[length:var(--text-micro)] uppercase tracking-widest ${
            isUser ? 'text-[color:var(--color-orion-haze)]' : 'text-[color:var(--color-orion-amber)]'
          }`}
        >
          {bubble.meta}
        </div>
        <div
          className={`rounded-[var(--radius-lg)] px-4 py-3 leading-relaxed ${
            isUser
              ? 'bg-[color:var(--color-orion-surface)]/70 text-[color:var(--color-orion-text)] rounded-tr-[6px]'
              : 'bg-[color:var(--color-orion-deep)]/80 border-faint text-[color:var(--color-orion-text)] rounded-tl-[6px]'
          }`}
          style={{ fontSize: 'var(--text-body)' }}
        >
          {bubble.text}
        </div>
        {!isUser && <Badge tone="info">{citationBadge}</Badge>}
      </div>
    </motion.div>
  )
}
